import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Context as CartContext } from '../../../../../context/cart';
import { Context as RestaurantContext } from '../../../../../context/restaurant';
import { Context as DeliveryContext } from '../../../../../context/delivery';
import { useMenuPositions } from '../../../hooks/useMenuPositions';
import MenuBar from '../../menuBar';
import { getSelectedCategory } from '../../../services/categories';
import ProductModal from '../productModal';
import { getTranslatedData } from '../../../../../localization';
import { useBackPress } from '../../../../../hooks/useBackPress';
import { useLocalStorage } from '../../../../../hooks/useLocalStorage';
import MenuInfoDelivery from './menuInfo';
import ProductDelivery from '../../product/productDelivery';
import CartPopupDelivery from '../../../../cart/components/delivery/cartPopupDelivery';
import TopBar from '../../../../../layouts/delivery/topBar';
import DeliveryModal from '../../../../../components/modal/deliveryModal';
import { useDeliveryMenuBarScroll } from '../../../hooks/useDeliveryMenuBarScroll';
import { useDeliveryCartTotal } from '../../../../../hooks/useDeliveryCartTotal';
import { useCurrency } from '../../../../../hooks/useCurrency';
import DeliveryViewOrder from '../../../../../components/buttons/deliveryViewOrder';
import Product from '../../product/product';

const OrderDeliveryMenu = ({ deliveryRestaurant, productCategories, onlyDisplay }) => {
  const navigate = useNavigate();
  const { t } = useTranslation(['translation', 'delivery']);
  const { currencySymbol } = useCurrency();

  const [cartVisible, setCartVisible] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredProductCategories, setFilteredProductCategories] = useState(productCategories);

  const { windowHeight, scrollPosition, categoriesY, categoriesRef, onImagePositionChange } =
    useMenuPositions(filteredProductCategories);

  const { state: cartState, addProductToDeliveryCart } = useContext(CartContext);
  const { state: restaurantState } = useContext(RestaurantContext);
  const { state: deliveryState } = useContext(DeliveryContext);

  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productDialogOpen, setProductDialogOpen] = useState(false);

  const [topCartLineHeight, setTopCartLineHeight] = useState(68);

  const { handleBackOnModalOpen, handleBackOnModalClose } = useBackPress(
    `/${deliveryState.slug}/${deliveryRestaurant.slug || deliveryRestaurant.id}`,
    () => setProductDialogOpen(false),
    'product=true',
  );

  const {
    handleBackOnModalOpen: handleBackOnCartOpen,
    handleBackOnModalClose: handleBackOnCartClose,
  } = useBackPress(
    `/${deliveryState.slug}/${deliveryRestaurant.slug || deliveryRestaurant.id}`,
    () => setCartVisible(false),
    'cart=true',
  );

  const [ageConfirmed, setAgeConfirmed] = useLocalStorage(
    `age-confirmed-${deliveryRestaurant.id}`,
    false,
  );

  const { searchBlockRef, menuBarRef, menuBarReached } = useDeliveryMenuBarScroll();
  const { totalAfterDiscount, totalInCart } = useDeliveryCartTotal();

  const onManualSelect = (categoryId) => {
    const categoryPosition = categoriesY[categoryId || ''];

    // eslint-disable-next-line no-undef
    window.scrollTo({
      top:
        categoryPosition -
        (menuBarRef && menuBarRef.current ? menuBarRef.current.offsetHeight : 0) -
        20,
      behavior: 'smooth',
    });
  };

  const renderCategoryMenu = (category, ind) => {
    const searchRegex = new RegExp(`.*${searchTerm}.*`, 'i');
    const showMobileProducts = global.window.innerWidth < 640;

    const restaurantCartData = cartState.deliveryCart[deliveryRestaurant.id] || {};
    const { cart } = restaurantCartData;

    return (
      <div
        key={`${ind}`}
        ref={(el) => {
          if (!el) {
            return;
          }

          const oldPos = categoriesRef.current[category.id];
          categoriesRef.current[category.id] = { offsetTop: el ? el.offsetTop : 0 };

          // Position change on initial load
          if (oldPos && oldPos.offsetTop !== el.offsetTop && ind === productCategories.length - 1) {
            onImagePositionChange();
          }
        }}
      >
        <h3 className="font-primaryBold text-primary text-2xl mb-6">
          {category.id === '_other'
            ? t('translation:products.categoryOther')
            : getTranslatedData(category.name)}
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-6 mb-10">
          {(category.products || []).map((product) => {
            let totalProductInCart = 0;
            if (cart) {
              cart.forEach((cartItem) => {
                if (cartItem.productId === product.id) {
                  totalProductInCart += cartItem.quantity;
                }
              });
            }

            if (searchTerm && !searchRegex.test(getTranslatedData(product.name))) {
              return null;
            }

            if (showMobileProducts) {
              return (
                <Product
                  key={product.id}
                  product={product}
                  onSelect={() => {
                    setSelectedProduct(product);
                    setProductDialogOpen(true);
                    handleBackOnModalOpen();
                  }}
                  totalInCart={totalProductInCart}
                />
              );
            }

            return (
              <ProductDelivery
                key={product.id}
                product={product}
                onSelect={() => {
                  setSelectedProduct(product);
                  setProductDialogOpen(true);
                  handleBackOnModalOpen();
                }}
                totalInCart={totalProductInCart}
              />
            );
          })}
        </div>
      </div>
    );
  };

  const selected = getSelectedCategory({
    windowHeight,
    scrollPosition,
    categoriesY,
    selectFirstIfNone: true,
    customThresholdFunc: () => {
      return (
        scrollPosition +
        (menuBarRef && menuBarRef.current ? menuBarRef.current.offsetHeight : 0) +
        50
      );
    },
  });

  return (
    <>
      <div>
        <TopBar
          onBack={
            deliveryState.restaurants && deliveryState.restaurants.length > 1
              ? () => {
                  navigate(`/${deliveryState.slug}`, { replace: true });
                }
              : undefined
          }
          showOrder
          onShowCart={() => {
            setCartVisible(true);
            handleBackOnCartOpen();
          }}
          showTypeTabs
          useStickyNav
          onTopCartLineHeightChange={(valH) => {
            setTopCartLineHeight(valH);
          }}
        />

        <div ref={searchBlockRef}>
          <MenuInfoDelivery
            deliveryRestaurant={deliveryRestaurant}
            onSearch={(newSearchTerm) => {
              if (newSearchTerm) {
                const searchRegex = new RegExp(`.*${newSearchTerm}.*`, 'i');
                const filteredCategories = (productCategories || []).filter((category) => {
                  const productsToShow = category.products.filter((product) =>
                    searchRegex.test(getTranslatedData(product.name)),
                  );

                  return productsToShow && productsToShow.length > 0;
                });

                setFilteredProductCategories(filteredCategories);
              } else {
                setFilteredProductCategories(productCategories);
              }
              setSearchTerm(newSearchTerm);
            }}
          />
        </div>

        <div
          ref={menuBarRef}
          className={`sticky bg-white z-10 ${menuBarReached ? 'shadow-md' : ''}`}
          style={{ top: topCartLineHeight }}
        >
          <div className="px-5">
            <MenuBar
              productCategories={filteredProductCategories || []}
              selected={selected}
              onSelect={onManualSelect}
              isDelivery
            />
          </div>
        </div>

        <div className="px-5">
          <div className="mt-6 max-w-6xl mx-auto w-full">
            {filteredProductCategories && filteredProductCategories.length > 0 ? (
              (filteredProductCategories || []).map((category, ind) => {
                return renderCategoryMenu(category, ind);
              })
            ) : (
              <div className="font-primaryMedium text-lg text-primary">
                {t(`delivery:menu.noItems`)}
              </div>
            )}
          </div>
        </div>
      </div>

      {totalInCart > 0 && (
        <div className="px-5 sticky bottom-4">
          <DeliveryViewOrder
            currencySymbol={currencySymbol}
            onShowCart={() => {
              setCartVisible(true);
              handleBackOnCartOpen();
            }}
            totalAfterDiscount={totalAfterDiscount}
            totalInCart={totalInCart}
          />
        </div>
      )}

      <CartPopupDelivery
        isVisible={cartVisible}
        onClose={() => {
          setCartVisible(false);
          handleBackOnCartClose();
        }}
        onSelectRecommendation={(product) => {
          setSelectedProduct(product);
          setProductDialogOpen(true);
          handleBackOnModalOpen();
        }}
        deliveryRestaurant={deliveryRestaurant}
      />

      <DeliveryModal
        content={
          <ProductModal
            product={selectedProduct}
            onAdd={(data) => {
              addProductToDeliveryCart(
                deliveryRestaurant.id,
                data,
                restaurantState.discountCartService,
              );
              setProductDialogOpen(false);
              handleBackOnModalClose();
            }}
            refresher={productDialogOpen}
            onClose={() => {
              setProductDialogOpen(false);
              handleBackOnModalClose();
            }}
            discountMenuService={restaurantState.discountMenuService}
            onlyDisplay={onlyDisplay}
            needConfirmAge={!ageConfirmed}
            restrictionsAge={deliveryRestaurant.data.restrictionsAge}
            onAgeConfirmed={() => setAgeConfirmed(true)}
            roundedCorners
            scrollable
          />
        }
        onOutClick={() => {
          setProductDialogOpen(false);
          handleBackOnModalClose();
        }}
        open={productDialogOpen}
        hasPreviousModalOpen={cartVisible}
      />
    </>
  );
};

export default OrderDeliveryMenu;
